/**
 * "Draft all sections" on a response document's Overview.
 *
 * Loads which sections one AI job may draft, starts that job, and follows it
 * to the end — including a job already running when the page opens, so a
 * reload or a second teammate sees the same progress instead of a live button.
 *
 * The server decides what gets drafted (empty and writable; a section with no
 * plan is planned first); this hook only asks for the sections it showed the user.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import {
  ApiRequestError,
  getDraftAllStatus,
  startDraftAll,
  toastApiError,
  type DraftAllSectionView,
  type DraftAllStatus,
} from '@/lib/api'
import { AgentClientError, pollAsyncJob, type AsyncJobResult } from '@/lib/agent-client'
import { useActiveJobsStore } from '@/stores/active-jobs-store'

// ===================
// Types
// ===================

export type DraftAllOutcome =
  | {
      kind: 'done'
      drafted: number
      remaining: number
      skipped: number
      /** Why sections were not drafted, one line per distinct reason ("Skipped Methodology: assigned to Sam Chen."). */
      skipReasons: string[]
    }
  | { kind: 'stopped'; drafted: number }
  | { kind: 'failed'; message: string }

export interface DraftAllRun {
  jobId: string
  sessionId: string
  progressMessage: string | null
}

export interface UseDraftAllResult {
  /** Null until the first status load lands (or when it failed). */
  status: DraftAllStatus | null
  /** Empty sections this user can have drafted now. */
  readySections: DraftAllSectionView[]
  /** Empty sections the AI may not write for this user. */
  notWritableCount: number
  /** Why each of those is skipped, one line per reason (a document-wide reason once). */
  skipReasons: string[]
  /** Any section without text — what enables the action at all. */
  emptyCount: number
  run: DraftAllRun | null
  isStarting: boolean
  outcome: DraftAllOutcome | null
  start: () => Promise<void>
  /** Re-read which sections are draftable (e.g. as the confirm dialog opens). */
  refresh: () => Promise<DraftAllStatus | null>
  dismissOutcome: () => void
}

// ===================
// Helpers
// ===================

function countIds(payload: Record<string, unknown> | null, key: string): number {
  const value = payload?.[key]
  return Array.isArray(value) ? value.length : 0
}

/** The job's AI-write-gate skip lines (`writeDenials[].reason`), one per distinct reason. */
function readWriteDenialReasons(payload: Record<string, unknown> | null): string[] {
  const denials = payload?.['writeDenials']
  if (!Array.isArray(denials)) return []
  const reasons = denials
    .map((denial) => (denial && typeof denial === 'object' ? (denial as { reason?: unknown }).reason : null))
    .filter((reason): reason is string => typeof reason === 'string' && reason.trim().length > 0)
  return Array.from(new Set(reasons))
}

function failureMessage(errorMessage: string | null): string {
  // The job poll's own reaper writes this when the worker went silent.
  if (!errorMessage || errorMessage === 'timeout_reaper') {
    return 'Drafting stopped responding. Sections drafted before it stopped are saved.'
  }
  return errorMessage
}

/** Turn a finished job into what the Overview tells the user. */
export function describeDraftAllResult(result: AsyncJobResult): DraftAllOutcome {
  const payload = result.resultPayload
  if (result.status === 'completed') {
    return {
      kind: 'done',
      drafted: countIds(payload, 'generatedSectionIds'),
      remaining: countIds(payload, 'remainingSectionIds'),
      skipped: countIds(payload, 'skippedSectionIds'),
      skipReasons: readWriteDenialReasons(payload),
    }
  }
  if (result.status === 'cancelled') {
    return { kind: 'stopped', drafted: countIds(payload, 'generatedSectionIds') }
  }
  return { kind: 'failed', message: failureMessage(result.errorMessage) }
}

function readString(details: Record<string, unknown> | undefined, key: string): string | null {
  const value = details?.[key]
  return typeof value === 'string' && value.length > 0 ? value : null
}

// ===================
// Hook
// ===================

export function useDraftAll(
  artifactId: string | undefined,
  options: {
    enabled: boolean
    /**
     * Changes whenever a saved outline change lands, so readiness is re-read.
     * Must track the server's copy (e.g. its version), not optimistic local edits.
     */
    outlineSignature: string
    onSettled: () => void
  }
): UseDraftAllResult {
  const { enabled, outlineSignature } = options
  const [status, setStatus] = useState<DraftAllStatus | null>(null)
  const [run, setRun] = useState<DraftAllRun | null>(null)
  const [isStarting, setIsStarting] = useState(false)
  const [outcome, setOutcome] = useState<DraftAllOutcome | null>(null)

  // Latest callback without re-running effects when the page re-renders.
  const onSettledRef = useRef(options.onSettled)
  onSettledRef.current = options.onSettled
  const pollRef = useRef<{ jobId: string; controller: AbortController } | null>(null)
  // Why sections were left out when this page started a run: never queued, so
  // the job cannot report them, but the completion notice still names them.
  const excludedAtStartRef = useRef<{ jobId: string; reasons: string[] } | null>(null)

  const refresh = useCallback(async (): Promise<DraftAllStatus | null> => {
    if (!artifactId) return null
    try {
      const next = await getDraftAllStatus(artifactId)
      setStatus(next)
      return next
    } catch {
      // The action simply stays unavailable; nothing else on the page depends on it.
      setStatus(null)
      return null
    }
  }, [artifactId])

  const follow = useCallback(
    (jobId: string, sessionId: string, progressMessage: string | null) => {
      if (pollRef.current?.jobId === jobId) return
      pollRef.current?.controller.abort()
      const controller = new AbortController()
      pollRef.current = { jobId, controller }
      setRun({ jobId, sessionId, progressMessage })
      setOutcome(null)

      void pollAsyncJob(
        jobId,
        sessionId,
        (_progress, message) => {
          if (controller.signal.aborted || !message) return
          setRun((current) => (current && current.jobId === jobId ? { ...current, progressMessage: message } : current))
        },
        controller.signal
      )
        .then((result) => {
          if (controller.signal.aborted) return
          const described = describeDraftAllResult(result)
          const excluded = excludedAtStartRef.current?.jobId === jobId ? excludedAtStartRef.current.reasons : []
          setOutcome(
            described.kind === 'done'
              ? { ...described, skipReasons: Array.from(new Set([...excluded, ...described.skipReasons])) }
              : described
          )
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted) return
          if (error instanceof AgentClientError && error.code === 'CANCELLED') return
          setOutcome({
            kind: 'failed',
            message: 'Lost track of the drafting job. Refresh the page to see what was drafted.',
          })
        })
        .finally(() => {
          if (controller.signal.aborted) return
          pollRef.current = null
          setRun(null)
          // Sections may have landed whatever the ending — show them.
          onSettledRef.current()
          void refresh()
        })
    },
    [refresh]
  )

  // A different document: forget the last one's state and stop following its
  // run (the aborted poll writes nothing back).
  useEffect(() => {
    setStatus(null)
    setRun(null)
    setOutcome(null)
    return () => {
      pollRef.current?.controller.abort()
      pollRef.current = null
    }
  }, [artifactId])

  // Load readiness — again whenever the outline changes — and pick up a run
  // already in progress (a reload, or a teammate started it).
  useEffect(() => {
    if (!enabled || !artifactId) return
    let cancelled = false
    void refresh().then((next) => {
      if (cancelled || !next?.activeJob) return
      follow(next.activeJob.jobId, next.activeJob.sessionId, next.activeJob.progressMessage)
    })
    return () => {
      cancelled = true
    }
  }, [enabled, artifactId, outlineSignature, refresh, follow])

  const readySections = useMemo(
    () => status?.sections.filter((section) => section.state === 'ready') ?? [],
    [status]
  )
  const notWritableSections = useMemo(
    () => status?.sections.filter((section) => section.state === 'not_writable') ?? [],
    [status]
  )
  const notWritableCount = notWritableSections.length
  const skipReasons = useMemo(
    // A server from before the AI write rule sends no reason: name the section, claim no cause.
    () => Array.from(new Set(notWritableSections.map((section) => section.skipReason ?? `Skipped ${section.title}.`))),
    [notWritableSections]
  )
  const emptyCount = readySections.length + notWritableCount

  const start = useCallback(async () => {
    if (!artifactId || readySections.length === 0 || run || isStarting) return
    setIsStarting(true)
    setOutcome(null)
    try {
      const started = await startDraftAll(
        artifactId,
        readySections.map((section) => section.sectionKey)
      )
      useActiveJobsStore.getState().notifyJobDispatched()
      excludedAtStartRef.current = { jobId: started.jobId, reasons: skipReasons }
      follow(started.jobId, started.sessionId, null)
    } catch (error) {
      if (error instanceof ApiRequestError && error.code === 'DRAFT_ALL_BUSY') {
        const jobId = readString(error.details, 'jobId')
        const sessionId = readString(error.details, 'sessionId')
        if (jobId && sessionId) {
          // Someone (maybe this user, in another tab) already started it — follow that run.
          follow(jobId, sessionId, null)
        } else {
          toast.info('AI is already working on this document. Try again when it finishes.')
        }
      } else if (error instanceof ApiRequestError && error.code === 'NOTHING_TO_DRAFT') {
        toast.info('Those sections changed, so there is nothing to draft now.')
        void refresh()
      } else {
        toastApiError(error, "Couldn't start drafting. Please try again.")
      }
    } finally {
      setIsStarting(false)
    }
  }, [artifactId, readySections, skipReasons, run, isStarting, follow, refresh])

  const dismissOutcome = useCallback(() => setOutcome(null), [])

  return {
    status,
    readySections,
    notWritableCount,
    skipReasons,
    emptyCount,
    run,
    isStarting,
    outcome,
    start,
    refresh,
    dismissOutcome,
  }
}
